import { CALCULATORS } from "@/lib/calculators/data";
import { FEATURE_CONTENT_MAP } from "@/lib/features/content";
import { TOOL_CONTENT_MAP } from "@/lib/tools/content";
import { USE_CASE_CONTENT_MAP } from "@/lib/use-cases/content";
import { GLOSSARY_TERMS, glossaryPath } from "@/lib/glossary/content";
import { IDEA_NICHES, ideaPath, ideaTitle } from "@/lib/ideas/content";
import { COMPARE_PATH, comparePath, getRoundups, getSpokes } from "@/lib/compare";
import { CAPTIONS_PATH, LANGUAGE_PAGES, languagePath } from "@/lib/languages/content";
import { CAPTION_STYLES, CAPTION_STYLES_PATH, captionStylePath } from "@/lib/caption-styles/content";
import { DOCS_PATH, DOC_PAGES, docPath } from "@/lib/docs/content";

export type MarketingPageKind =
  | "feature"
  | "tool"
  | "useCase"
  | "calculator"
  | "glossary"
  | "idea"
  | "compare"
  | "language"
  | "captionStyle"
  | "doc";

export type MarketingPage = {
  kind: MarketingPageKind;
  slug: string;
  path: string;
  title: string;
  description: string;
  image?: string;
};

export type MarketingHub = {
  kind: MarketingPageKind;
  path: string;
  label: string;
};

export const HUBS: Record<MarketingPageKind, MarketingHub> = {
  feature: { kind: "feature", path: "/features", label: "Features" },
  tool: { kind: "tool", path: "/features/free", label: "Free Tools" },
  useCase: { kind: "useCase", path: "/use-cases", label: "Use Cases" },
  calculator: { kind: "calculator", path: "/calculators", label: "Calculators" },
  glossary: { kind: "glossary", path: "/glossary", label: "Glossary" },
  idea: { kind: "idea", path: "/ideas", label: "Video Ideas" },
  compare: { kind: "compare", path: COMPARE_PATH, label: "Compare" },
  language: { kind: "language", path: CAPTIONS_PATH, label: "Caption Languages" },
  captionStyle: { kind: "captionStyle", path: CAPTION_STYLES_PATH, label: "Caption Styles" },
  doc: { kind: "doc", path: DOCS_PATH, label: "API Docs" },
};

export const HIDDEN_FEATURES = ["reddit-story"];

const featurePages = (): MarketingPage[] =>
  Object.entries(FEATURE_CONTENT_MAP).map(([slug, feature]) => ({
    kind: "feature" as const,
    slug,
    path: `/features/${slug}`,
    title: feature.title,
    description: feature.description,
    image: feature.heroImage,
  }));

const toolPages = (): MarketingPage[] =>
  Object.entries(TOOL_CONTENT_MAP).map(([slug, tool]) => ({
    kind: "tool" as const,
    slug,
    path: `/features/free/${slug}`,
    title: tool.navTitle,
    description: tool.heroDescription,
  }));

const getUseCasePages = (): MarketingPage[] =>
  Object.entries(USE_CASE_CONTENT_MAP).map(([slug, useCase]) => ({
    kind: "useCase" as const,
    slug,
    path: `/use-cases/${slug}`,
    title: useCase.navTitle,
    description: useCase.description,
  }));

const calculatorPages = (): MarketingPage[] =>
  CALCULATORS.map((calculator) => ({
    kind: "calculator" as const,
    slug: calculator.slug,
    path: `/calculators/${calculator.slug}`,
    title: calculator.cardTitle,
    description: calculator.description,
  }));

const glossaryPages = (): MarketingPage[] =>
  Object.entries(GLOSSARY_TERMS).map(([slug, term]) => ({
    kind: "glossary" as const,
    slug,
    path: glossaryPath(slug),
    title: term.term,
    description: term.definition,
  }));

const ideaPages = (): MarketingPage[] =>
  IDEA_NICHES.map((niche) => ({
    kind: "idea" as const,
    slug: niche.slug,
    path: ideaPath(niche.slug),
    title: ideaTitle(niche),
    description: niche.seoDescription,
  }));

const comparePages = (): MarketingPage[] => [
  ...getSpokes().map(({ content }) => ({
    kind: "compare" as const,
    slug: content.slug,
    path: comparePath(content.slug),
    title: content.title,
    description: content.seoDescription,
  })),
  ...getRoundups().map((roundup) => ({
    kind: "compare" as const,
    slug: roundup.slug,
    path: comparePath(roundup.slug),
    title: roundup.title,
    description: roundup.seoDescription,
  })),
];

const languagePages = (): MarketingPage[] =>
  Object.entries(LANGUAGE_PAGES).map(([slug, language]) => ({
    kind: "language" as const,
    slug,
    path: languagePath(slug),
    title: `${language.name} captions`,
    description: language.seoDescription,
  }));

const captionStylePages = (): MarketingPage[] =>
  CAPTION_STYLES.map((style) => ({
    kind: "captionStyle" as const,
    slug: style.slug,
    path: captionStylePath(style.slug),
    title: style.name,
    description: style.seoDescription,
    image: style.thumbnail,
  }));

const docPages = (): MarketingPage[] =>
  DOC_PAGES.map((page) => ({
    kind: "doc" as const,
    slug: page.slug,
    path: docPath(page.slug),
    title: page.title,
    description: page.seoDescription,
  }));

let allPages: MarketingPage[] | undefined;

const getAllPages = (): MarketingPage[] =>
  (allPages ??= [
    ...featurePages(),
    ...toolPages(),
    ...getUseCasePages(),
    ...calculatorPages(),
    ...glossaryPages(),
    ...ideaPages(),
    ...comparePages(),
    ...languagePages(),
    ...captionStylePages(),
    ...docPages(),
  ]);

const isHidden = (page: MarketingPage) =>
  page.kind === "feature" && HIDDEN_FEATURES.includes(page.slug);

export const getMarketingPages = (kind?: MarketingPageKind): MarketingPage[] =>
  getAllPages().filter((page) => !isHidden(page) && (!kind || page.kind === kind));

export const getMarketingPage = (path: string): MarketingPage | undefined =>
  getAllPages().find((page) => page.path === path);
